import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModuleSection } from '@platform/entitlements';
import {
  getSkipTarget,
  isSkipOn,
  setSkip,
  setSkipTarget,
  SKIP_TARGETS,
} from './skipStart';
import './StartPage.css';

/**
 * Strategic Portfolio Navigator — the module's Start Menu.
 *
 * CONTENT is the source's, from `spn_template/views/start-menu.ejs`: the greeting, the
 * product name, the tagline and the copy on what is shown, verbatim.
 *
 * DESIGN is this platform's (owner's ruling, 2026-08-04). The source renders its own
 * look; here it uses the same hero, journey, block labels, tiles and support strip as APR
 * and AI Readiness, so all three read as one product rather than three apps behind one
 * login.
 *
 * ONLY THE DISCOVER PHASE IS SHOWN (owner's ruling, 2026-08-04). The source's other six
 * stages and its Financials, Planning and remaining Strategy tiles are removed rather than
 * dimmed: some of that work stays in the source app and some is going to OTHER products
 * in this platform, so showing it here would promise something this module is not going
 * to deliver. They are deleted, not commented out — the source file remains the record of
 * what the original screen offered, and a stage returns here only if it is actually built
 * for this module.
 */

/** Where "Contact your Navigator advisor" goes — the same support inbox as the others. */
const ADVISOR_MAILTO =
  'mailto:support@talogent.com' +
  '?subject=' +
  encodeURIComponent('Strategic Portfolio Navigator — request for assistance') +
  '&body=' +
  encodeURIComponent(
    'Tell us what you need help with and we will get back to you.\n\n' +
      '(Sent from Strategic Portfolio Navigator)\n',
  );

/**
 * The journey, reduced to the one stage this module delivers.
 *
 * The source runs seven — Discover, Score, Finance, Optimize, Plan, Execute, Monitor.
 * Only Discover is here.
 */
const JOURNEY: ReadonlyArray<{ step: number; label: string; path?: string }> = [
  { step: 1, label: 'Discover', path: '/spn/discoveries' },
  // Transfer is the END of what this module does, not another stage of analysis: the
  // opportunity leaves for Inventory Registry and is locked here. Shown as a step so the
  // start screen says where the work goes, which nothing else on it did.
  { step: 2, label: 'Transfer', path: '/spn/transfer' },
];

interface Tile {
  title: string;
  description: string;
  path?: string;
}

/** The one group that has a screen. Heading and copy are the source's. */
const GROUPS: ReadonlyArray<{ heading: string; tiles: Tile[] }> = [
  {
    heading: 'Strategy & Scoring',
    tiles: [
      {
        title: 'Strategic Intelligence',
        description: 'Discoveries, scoring frameworks & analysis',
        path: '/spn/discoveries',
      },
    ],
  },
  {
    heading: 'Handover',
    tiles: [
      {
        title: 'Transfer',
        // Says what it costs as well as what it does — locking is the consequential half
        // and a tile that only promised the transfer would be selling one side of it.
        description: 'Hand settled opportunities to Opportunity Registry — locked here once sent',
        path: '/spn/transfer',
      },
    ],
  },
];

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path d="M5 12h13M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export interface SpnStartPageProps {
  firstName?: string;
  sections: readonly ModuleSection[];
}

export function SpnStartPage({ firstName }: SpnStartPageProps) {
  const navigate = useNavigate();

  /* The preference and its destination, seeded from storage so the bar shows what is
     actually saved rather than a default that disagrees with it. */
  const [skip, setSkipOn] = useState<boolean>(() => isSkipOn());
  const [skipTarget, setTarget] = useState<string>(() => getSkipTarget());

  const toggleSkip = (on: boolean) => {
    setSkipOn(on);
    setSkip(on);
    // Turning it ON also writes the destination, so the preference is never a flag with
    // nowhere to go — which is what "skip" meant in APR before it was fixed, and it did
    // nothing at all.
    if (on) setSkipTarget(skipTarget);
  };

  const chooseTarget = (path: string) => {
    setTarget(path);
    setSkipTarget(path);
  };

  const targetLabel =
    SKIP_TARGETS.find((t) => t.path === skipTarget)?.label ?? SKIP_TARGETS[0].label;

  return (
    <div className="spn-start">
      <span className="hello">Welcome{firstName ? `, ${firstName}` : ''}</span>
      <h1>Strategic Portfolio Navigator</h1>
      <p className="sub">
        From opportunity to outcome — evaluate and score initiatives, model cost and value,
        optimize the portfolio, then plan and execute delivery you can track.
      </p>

      {/* One stage. See the note at the head of this file on why the other six are
          absent rather than dimmed. */}
      <div className="block-label">
        <h2>The journey</h2>
        <span className="rule"></span>
        <span className="count">1 stage</span>
      </div>
      <div className="journey">
        {JOURNEY.map((j) => (
          <button
            key={j.step}
            className={'stage' + (j.path ? '' : ' inert')}
            onClick={j.path ? () => navigate(j.path!) : undefined}
            disabled={!j.path}
          >
            <span className="stepno">Step {j.step}</span>
            <span className="steplabel">{j.label}</span>
          </button>
        ))}
      </div>

      {GROUPS.map((g) => (
        <div key={g.heading}>
          <div className="block-label">
            <h2>{g.heading}</h2>
            <span className="rule"></span>
          </div>
          <div className="quick">
            {g.tiles.map((t) => (
              <button
                key={t.title}
                className={'tile' + (t.path ? '' : ' inert')}
                onClick={t.path ? () => navigate(t.path!) : undefined}
                disabled={!t.path}
              >
                <span>
                  <b>{t.title}</b>
                  <span>{t.description}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Two destinations, because SPN has two screens. Both are real screens rather than
          buckets, so skipping lands on something you can work in rather than another menu. */}
      <div className={'skipbar' + (skip ? ' on' : '')}>
        <label className="switch">
          <input type="checkbox" checked={skip} onChange={(e) => toggleSkip(e.target.checked)} />
          <span className="sw">
            <span className="knob" />
          </span>
          <span className="lab">Skip this welcome screen next time</span>
        </label>

        <span className="txt">
          {skip ? (
            <>
              Saved — entering this module opens straight to <b>{targetLabel}</b>. Take me to:
            </>
          ) : (
            <>
              Turn this on and you&apos;ll skip this welcome screen, going straight to the
              screen you choose. Turn it off here at any time to bring it back.
            </>
          )}
        </span>

        <label className="skip-target">
          <span className="skip-target-lab">Open</span>
          <select
            value={skipTarget}
            onChange={(e) => chooseTarget(e.target.value)}
            aria-label="Screen to open when the welcome screen is skipped"
          >
            {SKIP_TARGETS.map((t) => (
              <option key={t.path} value={t.path}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="support">
        <p>
          <b>Need Assistance?</b> Our team runs working sessions with you — the platform guides
          you, and you&apos;re never on your own.
        </p>
        <a className="btn btn-ghost cta" href={ADVISOR_MAILTO}>
          Contact your Navigator advisor <Arrow />
        </a>
      </div>

    </div>
  );
}
